import type { HTMLAttributes, ReactNode } from 'react';
import { cx } from './cx';

interface C1CardProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  /** Single-column surface without the two-column login layout. */
  plain?: boolean;
  /** Tighter padding for in-app panels. */
  compact?: boolean;
}

/**
 * The C1 card surface — either the two-column login shell (`children` as
 * brand + body) or a plain elevated panel (`plain`).
 */
export function C1Card({ children, plain = false, compact = false, className, ...props }: C1CardProps) {
  return (
    <div
      className={cx('c1-card', plain && 'c1-card--plain', compact && 'c1-card--compact', className)}
      {...props}
    >
      {children}
    </div>
  );
}

interface C1CardBrandProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  /** Logo URL; defaults to the public CampusOne mark. */
  logoSrc?: string;
  logoAlt?: string;
}

/** Left column: wordmark + title/subtitle. */
export function C1CardBrand({
  children,
  logoSrc = '/assets/images/campusone_logo.png',
  logoAlt = 'CampusOne',
  className,
  ...props
}: C1CardBrandProps) {
  return (
    <div className={cx('c1-left', className)} {...props}>
      <img className="c1-logo" src={logoSrc} alt={logoAlt} />
      {children}
    </div>
  );
}

interface C1CardBodyProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

/** Right column: form / step content. */
export function C1CardBody({ children, className, ...props }: C1CardBodyProps) {
  return (
    <div className={cx('c1-right', className)} {...props}>
      {children}
    </div>
  );
}
